import React from "react";

import AlertCircleIcon from "@uui/AlertCircle";

import { SvgIconProps, Typography, Button } from "@mui/material";
import styled from "@mui/material/styles/styled";

import {
  getBillingCallToAction,
  getTruncatedPlanLabel,
} from "@app/analytics/account/Billing/utils";
import TrialCountdownChip from "@app/analytics/components/TrialCountdownChip";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import {
  EnterpriseIcon,
  ProIcon,
  ZoomTrialIcon,
  FreePlanIcon,
} from "@app/common/assets/icons";
import ROUTES from "@app/common/routes";
import type { BillingCurrentPlan, BillingPlan } from "@app/common/types";

import { Link } from "@rui/Link";
import Stack, { Row, StackProps } from "@rui/Stack";

const ICON_FOR_PLAN: {
  [key in BillingPlan]: React.FunctionComponent<SvgIconProps>;
} = {
  enterprise_plus: EnterpriseIcon,
  enterprise: EnterpriseIcon,
  pro: ProIcon,
  zoom_bundle_trial: ZoomTrialIcon,
  free: FreePlanIcon,
  unknown: AlertCircleIcon,
};

const PlanLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.bodySecondaryMed,
  minWidth: "60px",
  paddingLeft: theme.spaces.xsmall,
}));

const PlanCtaButton = styled(Button)(({ theme }) => ({
  color: "white",
  padding: theme.spacing(0.5, 1),
  height: "24px",
  borderRadius: theme.corners.small,
  background: theme.palette.transparent.white[8],
}));

type PlanLinkWrapperProps = StackProps & { highlighted: boolean };

const PlanLinkWrapper = styled<React.FC<PlanLinkWrapperProps>>(
  ({ highlighted: _h, ...props }) => <Stack {...props} />
)(({ highlighted, theme }) => ({
  borderRadius: "8px",
  paddingBottom: theme.spacing(1),
  transition: "padding 0.15s ease",
  ...(highlighted && {
    padding: theme.spacing(1),
    paddingLeft: 0,
    backgroundColor: theme.palette.transparent.white[16],
  }),
}));

const PlanInfo = ({
  currentPlan,
  expanded,
  isLoading,
  isError,
}: {
  currentPlan: BillingCurrentPlan;
  expanded: boolean;
  isLoading?: boolean;
  isError?: boolean;
}) => {
  const Icon = ICON_FOR_PLAN[currentPlan.type];

  return (
    <Row
      variant="left"
      spacing="6px"
      sx={{ mr: "auto", height: "18px", transition: "margin 0.1s ease" }}
    >
      {isLoading ? (
        <Typography variant="bodySecondaryReg" color="shades.neutral.50">
          {expanded ? "Loading plan..." : "..."}
        </Typography>
      ) : (
        <>
          <Icon
            sx={{
              fontSize: "16px",
              color: isError ? "error.light" : "#4361EE",
            }}
          />
          <PlanLabel
            data-test-id="side-nav-billing-plan"
            noWrap
            sx={{
              color: isError ? "error.light" : "#FFF",
              visibility: expanded ? "visible" : "hidden",
            }}
          >
            {isError
              ? "Error loading plan"
              : getTruncatedPlanLabel(currentPlan.type)}
          </PlanLabel>
        </>
      )}
    </Row>
  );
};

const PlanLink = ({
  canEditBilling,
  expanded,
  highlighted,
  onClick,
}: {
  canEditBilling: boolean;
  expanded: boolean;
  highlighted: boolean;
  onClick?: () => void;
}) => {
  const {
    currentPlan,
    trialDaysRemaining,
    isTrial,
    plansInitialLoading,
    planLoadFailed,
    subscription,
  } = useBillingContext();

  const callToAction = getBillingCallToAction(currentPlan, isTrial);

  const showCountdownChip =
    subscription.inTrialPeriod || currentPlan.type === "zoom_bundle_trial";
  const showCta =
    canEditBilling && !plansInitialLoading && callToAction != null;

  return (
    <PlanLinkWrapper spacing={1} highlighted={highlighted}>
      <Row
        variant="apart"
        sx={{
          height: showCta ? "26px" : "18px",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Row flex={1} overflow="hidden" minWidth="25px">
          <Link
            to={ROUTES.BILLING}
            sx={{ py: 1, pl: "10px", pr: "2px" }}
            noWrap
            onClick={onClick}
          >
            <PlanInfo
              currentPlan={currentPlan}
              expanded={expanded}
              isLoading={plansInitialLoading}
              isError={planLoadFailed}
            />
          </Link>
        </Row>
        {showCta && (
          <Link
            to={callToAction.url}
            sx={{ visibility: expanded ? "visible" : "hidden" }}
          >
            <PlanCtaButton>{callToAction.title}</PlanCtaButton>
          </Link>
        )}
      </Row>
      {showCountdownChip && (
        <Stack
          paddingLeft={expanded ? "8px" : 0}
          sx={{ transition: "padding 0.15s ease" }}
        >
          <TrialCountdownChip
            daysRemaining={trialDaysRemaining}
            expanded={expanded}
            textColor="#FFF"
            backgroundColor="#2F2854"
          />
        </Stack>
      )}
    </PlanLinkWrapper>
  );
};

export default PlanLink;
